// constants/tokens.js
// Created: 2026-07-20
// Project: Giving Words to Voices
// Version: 1.0.0
//
// The token contract. Scheme-INDEPENDENT scales (spacing, radius, type,
// elevation, z-index, focus) plus helpers that derive scheme-DEPENDENT values
// (status fill/border, speaker colours) from the active theme.
//
// The point of this file: give components one small set of choices to reach
// for, instead of authoring a fresh px/hex value at every call site. The review
// measured the cost of not having it — 32 padding values, 19 radii, 27 z-index
// values, 55 font sizes, 517 button styles. Everything here is additive; nothing
// renders differently until a component opts in.
//
// The three colour schemes (constants/theme.js) supply the palette; this supplies
// the structure. A scheme is a short fill; this is the shape.

// ── Spacing — 4px base, the only ladder ──────────────────────────────────────
export const SPACE = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
  7: '48px',
  8: '64px',
};

// ── Radius — three steps plus the two shape values ───────────────────────────
export const RADIUS = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  pill: '999px',
  circle: '50%',
};

// ── Type — a 1.2 scale for chrome. Editor CONTENT keeps its user-scalable
//    sizes (theme.fs*); this is for labels, buttons, headings, badges. ────────
export const TYPE = {
  xs: '0.6875rem',   // 11px — badges, micro-labels
  sm: '0.8125rem',   // 13px — secondary
  base: '0.875rem',  // 14px — body
  md: '1rem',        // 16px
  lg: '1.25rem',     // 20px — section heading
  xl: '1.5rem',      // 24px
  '2xl': '2rem',     // 32px — page title
};

// Weight and tracking, so uppercase labels stop each picking their own.
export const WEIGHT = { normal: 400, medium: 500, semibold: 600, bold: 700 };
export const TRACKING = { tight: '-0.01em', normal: '0', label: '0.06em', caps: '0.09em' };

// ── Elevation — three levels, tuned per mode (dark needs deeper shadow) ───────
export function elevation(isDark) {
  return isDark
    ? {
        sm: '0 1px 3px rgba(0,0,0,0.4)',
        md: '0 6px 20px rgba(0,0,0,0.45)',
        lg: '0 18px 48px rgba(0,0,0,0.55)',
      }
    : {
        sm: '0 1px 3px rgba(0,0,0,0.10)',
        md: '0 6px 20px rgba(15,23,42,0.12)',
        lg: '0 18px 48px rgba(15,23,42,0.18)',
      };
}

// ── Z-index — six named layers, replacing 27 arbitrary values ────────────────
//
// Order encodes intent, and fixes two §03 defects the moment components adopt
// it: the idle-timeout warning now sits ABOVE modals (it interrupts them), and
// toasts sit BELOW dialogs (a confirm must never be hidden by a toast — the old
// toast at 99999 covered dialogs at 12000).
export const Z = {
  base: 0,
  raised: 1,
  sticky: 100,
  dropdown: 700,
  overlay: 1000,    // backdrops
  modal: 2000,      // feature modals
  palette: 2200,    // command / quick-switch palettes — over modals, under toasts
  toast: 2500,      // above modals, below dialogs
  dialog: 3000,     // confirm / prompt — above toasts
  idle: 3500,       // session-idle warning — above everything, it interrupts
};

// ── Focus — one ring, defined once. The CSS lives in index.css; this is the
//    colour, published to a CSS var from the active theme. ───────────────────
export const FOCUS = {
  width: '2px',
  offset: '2px',
  cssVar: '--gwv-focus',
};

// ── Status variants — text + fill + border from a single status colour ───────
//
// success/warning/danger/info arrive from the scheme as one colour each. A
// badge or banner needs three: the text colour, a faint fill behind it, and a
// border. Deriving them here means every status surface renders the same way
// instead of each inventing its own alpha suffix (the review found `#f59e0b`
// used with 15 different alpha values).
export function statusVariant(color, isDark) {
  const c = String(color || '#888');
  return {
    text: c,
    // Faint tint for the fill; a touch stronger on dark so it reads.
    fill: c + (isDark ? '26' : '1f'),   // ~15% / ~12%
    border: c + (isDark ? '66' : '55'), // ~40% / ~33%
  };
}

// ── Speaker palette — the single source, mode-aware and contrast-validated ───
//
// Light hues for dark backgrounds, dark hues for light. Each clears WCAG AA
// (4.5:1) as pill label text on every scheme surface in the matching mode
// (dark worst 6.6:1, light 4.79:1). Avoids the hues that carry meaning —
// green=done, amber=disagree, red=delete, purple=compare. Was duplicated inline
// in RightColumn; now one definition.
export const SPEAKER_PALETTE = {
  dark: ['#8ab4ff', '#4fd1c5', '#f0a6d0', '#e6c15a', '#5fc9e8'],
  light: ['#1d4ed8', '#0f6b61', '#a01a5b', '#8a5a12', '#0e6b85'],
};

export function speakerColor(index, isDark) {
  const pal = isDark ? SPEAKER_PALETTE.dark : SPEAKER_PALETTE.light;
  return pal[index % pal.length];
}

// ── Role palette — categorical identity colours, one definition ──────────────
//
// A role's colour is fixed (admin is always amber), so these are scheme-
// INDEPENDENT hues, not derived from the theme. They were duplicated
// byte-for-byte in App.js and DashboardPanel; this is now the single source.
// Each entry already carries its own tinted fill and border, matching how the
// badges were authored inline.
export const ROLE_COLORS = {
  admin:    { bg: 'rgba(245,158,11,0.14)', border: 'rgba(245,158,11,0.34)', text: '#f59e0b' },
  editor:   { bg: 'rgba(37,99,235,0.14)',  border: 'rgba(37,99,235,0.34)',  text: '#2563eb' },
  reviewer: { bg: 'rgba(124,58,237,0.14)', border: 'rgba(124,58,237,0.34)', text: '#7c3aed' },
  viewer:   { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.28)', text: '#64748b' },
};

// ── Mode from a theme — the one place to ask "is this dark?" ──────────────────
// Reads the background's relative luminance, so it follows the light/dark toggle
// with no separate flag.
export function isDarkTheme(theme) {
  const h = String((theme && theme.bg) || '#111').replace('#', '');
  if (h.length < 6) return true;
  const ch = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2] < 0.4;
}
